import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getAccessToken } from '@/shared/utils/supabaseStorage';

export interface TeamMember {
  id: string;
  name: string;
  email?: string;
  role: 'admin' | 'user';
}

interface TeamMembersContextType {
  teamMembers: TeamMember[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const TeamMembersContext = createContext<TeamMembersContextType | undefined>(undefined);

export function TeamMembersProvider({ children }: { children: ReactNode }) {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(false); // Start as false - only set true when actually fetching
  const [error, setError] = useState<string | null>(null);

  const fetchTeamMembers = async () => {
    try {
      // CRITICAL FIX: Get token from localStorage using shared utility
      const accessToken = getAccessToken();

      // Skip fetch if not authenticated (user is on login/public page)
      if (!accessToken) {
        // Silent for public/guest users - prevents console spam
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);
      console.log('[TeamMembersContext] Fetching team members...');

      // Call backend API to get team members
      const SIGNALING_SERVER_URL = import.meta.env.VITE_SIGNALING_SERVER_URL ?? 'http://localhost:3000';

      const response = await fetch(`${SIGNALING_SERVER_URL}/api/team/members`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to load team members' }));
        console.warn('[TeamMembersContext] API error (non-critical):', errorData);

        // Don't throw - just log and set error state
        // This prevents the error from bubbling up and crashing the app
        setError(errorData.error || 'Failed to load team members');
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (!data.ok || !data.members) {
        console.warn('[TeamMembersContext] Invalid response (non-critical):', data);
        setError('Invalid response from server');
        setLoading(false);
        return;
      }

      // Map backend response to TeamMember format
      const members: TeamMember[] = data.members.map((member: any) => {
        // Treat "unknown" as empty/null
        const fullName = member.full_name && member.full_name.toLowerCase() !== 'unknown'
          ? member.full_name
          : null;

        return {
          id: member.user_id,
          name: fullName || member.email || 'Team Member',
          email: member.email,
          role: member.role as 'admin' | 'user',
        };
      });

      console.log('[TeamMembersContext] ✅ Fetched team members:', members.length);
      setTeamMembers(members);
      setError(null);
    } catch (err) {
      // Catch any unexpected errors and prevent them from crashing the app
      console.warn('[TeamMembersContext] Unexpected error (non-critical):', err);
      setError(err instanceof Error ? err.message : 'Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Wrap in try-catch to prevent any errors from bubbling up
    try {
      fetchTeamMembers();
    } catch (err) {
      console.error('[TeamMembersContext] Error in useEffect:', err);
    }
  }, []);

  return (
    <TeamMembersContext.Provider value={{ teamMembers, loading, error, refetch: fetchTeamMembers }}>
      {children}
    </TeamMembersContext.Provider>
  );
}

export function useTeamMembersContext() {
  const context = useContext(TeamMembersContext);
  if (context === undefined) {
    throw new Error('useTeamMembersContext must be used within a TeamMembersProvider');
  }
  return context;
}
